// ─────────────────────────────────────────────────────────────────────────────
// ONE PLACE FOR EVERYTHING WAITING ON HER.
//
// Carla, 2026-09-22: merge "Attention Required" and "Notifications" — one
// sidebar item, one page, one count. Before this the sidebar carried both, with
// two badges over two unrelated tables: notifications (the bell) and event
// proposals (Attention Required, Block 208).
//
// HER RULE, which this page is built around: "An urgent or high-priority notice
// must never be dismissed because chat mentioned it. It stays under 'Needs
// attention' until I have acted on it. Chat may surface it; only my action
// clears it." So the top section is everything actionable or urgent, and
// nothing in it clears itself — see utils/notificationAction.needsDecision and
// lib/chatNotifications.js, which no longer auto-reads a high-priority notice.
//
// Block J (UI-22, 2026-10-11): restyled on the v2 kit (list and card patterns); decision 54 names the
// top section "Needs attention", with pending event proposals at its top. Fixed with it, failing
// first in BlockJNotifications.test.js: a failed load of the notifications, or of the proposals
// count, read as "Nothing needs your attention" or as a blank page. Each now says so, with the
// server's reason and a way to try again. Times are real instants (utils/formatting formatDateTime).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import EventProposalsBanner from './EventProposalsBanner';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { openNotification, needsDecision } from '../utils/notificationAction';
import { personalFlag } from '../nav/views';
import { messageFromError } from '../utils/apiError';
import { formatDateTime } from '../utils/formatting';
import { Banner, Button, EmptyState, ErrorState, PageHeader, PageLayout, StatusPill } from '../ui';
import './notifications.css';

export default function NotificationsPage({ setFilter }) {
  const { notifications, notificationCounts, notificationsLoadFailed, loadNotifications, markNotificationAsRead, markAllNotificationsAsRead, dismissNotification, workspaces, activeWorkspaceId } = useCurrentUser();
  const [proposals, setProposals] = useState({ count: null, error: null });

  const loadCount = useCallback(() => {
    fetchCount().then(
      (count) => setProposals({ count, error: null }),
      (err) => setProposals({ count: null, error: messageFromError(err, 'Suggestions from your email did not load.') }),
    );
  }, []);
  useEffect(() => {
    loadCount();
    window.addEventListener('aidey:tasksUpdated', loadCount);
    return () => window.removeEventListener('aidey:tasksUpdated', loadCount);
  }, [loadCount]);

  const live     = (notifications || []).filter(n => !n.dismissed);
  const decide   = live.filter(needsDecision);
  const rest     = live.filter(n => !needsDecision(n));
  // Fresh-eyes 11 (2026-10-12): the two numbers are the server's (lib/notificationCounts, held by the
  // context), the same ones the bell and the Dashboard tile show. They were counted here from the 50
  // rows loaded, plus the suggestions, and disagreed with both.
  const counted  = !!notificationCounts;
  const unread   = counted ? notificationCounts.unread : 0;
  const attention = counted ? notificationCounts.needsAttention : 0;
  const proposalCount = proposals.count || 0;
  // "Nothing" only when every source answered and all of them were empty.
  const nothing  = !notificationsLoadFailed && counted && attention === 0 && !proposals.error && proposals.count === 0 && decide.length === 0 && rest.length === 0;

  async function open(n) {
    if (!n.read) await markNotificationAsRead(n.id);
    openNotification(n, setFilter, personalFlag(workspaces, activeWorkspaceId));
  }

  // Carla, 2026-10-21: the needs-attention NUMBER is the Dashboard card's alone; this page lists
  // what needs attention, and says the unread count, the same number as the bell.
  const subline = notificationsLoadFailed || !counted
    ? 'Everything waiting on you, in one place.'
    : `${unread} unread`;

  return (
    <PageLayout data-page="notifications">
      <PageHeader title="Notifications" subline={subline}
        secondary={unread > 0 ? [{ label: 'Mark all as read', onClick: markAllNotificationsAsRead }] : []} />

      {notificationsLoadFailed && (
        <ErrorState title="Your notifications did not load"
          message="This is not an empty list: we could not reach your notifications, so something may be waiting. Check your connection and try again."
          onRetry={loadNotifications} />
      )}

      {proposals.error && (
        <Banner tone="problem" action={<Button size="sm" variant="secondary" onClick={loadCount}>Try again</Button>}>
          {proposals.error}
        </Banner>
      )}

      {/* ── NEEDS ATTENTION (decision 54) ───────────────────────────────── */}
      {(decide.length > 0 || proposalCount > 0 || attention > 0) && (
        <Section title="Needs attention" color="var(--pastel-yellow)" count={null}
          hint="These stay here until you act on them. Talking about one in chat does not clear it.">
          <EventProposalsBanner />
          {decide.map(n => (
            <Row key={n.id} n={n} onOpen={() => open(n)} onDismiss={dismissNotification ? () => dismissNotification(n.id) : null} urgent />
          ))}
        </Section>
      )}

      {/* ── EVERYTHING ELSE ──────────────────────────────────────────────── */}
      {rest.length > 0 && (
        <Section title="Recent" color="var(--pastel-mint)" count={rest.length}>
          {rest.map(n => (
            <Row key={n.id} n={n} onOpen={() => open(n)} onDismiss={dismissNotification ? () => dismissNotification(n.id) : null} />
          ))}
        </Section>
      )}

      {nothing && (
        <div data-testid="nothing-waiting">
          <EmptyState icon="✅" title="Nothing needs your attention">
            Anything that needs a decision, a client reply, an approval, an event Aidey spots in your email, appears here.
          </EmptyState>
        </div>
      )}
    </PageLayout>
  );
}

function Section({ title, hint, color, count, children }) {
  return (
    <section className="nt-section" style={{ '--nt-tint': color }}>
      <div className="nt-section__head">
        <h2 className="nt-section__title">{title}</h2>
        {count != null && <StatusPill tone="neutral">{count}</StatusPill>}
      </div>
      {hint && <p className="nt-section__hint">{hint}</p>}
      <div className="nt-section__body">{children}</div>
    </section>
  );
}

function Row({ n, onOpen, onDismiss, urgent = false }) {
  return (
    <div data-testid={urgent ? 'decision-row' : 'recent-row'} className={`nt-row${n.read ? '' : ' nt-row--unread'}`}>
      <button type="button" className="nt-row__open" onClick={onOpen}>
        <span className="nt-row__title">
          {!n.read && <span className="nt-row__dot" aria-label="Unread" />}
          {n.title}
        </span>
        {n.message && <span className="nt-row__message">{n.message}</span>}
        <span className="nt-row__meta">
          {n.createdAt ? formatDateTime(n.createdAt) : ''}
          {urgent && (n.actionable ? <StatusPill tone="pending">Action needed</StatusPill> : <StatusPill priority="high" />)}
        </span>
      </button>
      {/* Dismissing is HER action, so it is allowed here and nowhere automatic. */}
      {onDismiss && (
        <Button size="sm" variant="tertiary" onClick={onDismiss} aria-label={`Dismiss: ${n.title}`}>Dismiss</Button>
      )}
    </div>
  );
}

// Cheap indexed count — same predicate as the list, no rows returned. Throws when it cannot be read.
async function fetchCount() {
  const res = await api('/api/event-proposals/count');
  const d = await res.json();
  if (typeof d?.count !== 'number') throw new Error('Suggestions from your email could not be counted.');
  return d.count;
}
